import { FlattenErrors } from './components/ui/ErrorComponent';
import { appInsights } from './appInsights';
import { Api } from './_generatedTypes.g';

export type ApiInstance = ReturnType<typeof api>;
function getRequestFactory(requestVerificationToken: string) {
    const headers = {
        'Content-Type': 'application/json',
        'RequestVerificationToken': requestVerificationToken!
    };
    const params = (method: string, body: object) => ({
        method,
        headers,
        credentials: 'same-origin' as 'same-origin',
        body: JSON.stringify(body),
    })
    return {
        post<TReturnValue>(route: string, body: object) {
            return body instanceof FormData
                ? new ApiRequest<TReturnValue>(fetch(route, {
                    method: 'POST',
                    headers: { 'RequestVerificationToken': requestVerificationToken! },
                    body,
                    credentials: 'same-origin',
                }))
                : new ApiRequest<TReturnValue>(fetch(route, params('POST', body)));
        },
        put<TReturnValue>(route: string, body: object) {
            return new ApiRequest<TReturnValue>(fetch(route, params('PUT', body)));
        },
        delete<TReturnValue>(route: string, body: object) {
            return new ApiRequest<TReturnValue>(fetch(route, params('DELETE', body)));
        },
        patch<TReturnValue>(route: string, body: object) {
            return new ApiRequest<TReturnValue>(fetch(route, params('PATCH', body)));
        },
        get<TReturnValue>(route: string, accept?: string) {
            return new ApiRequest<TReturnValue>(fetch(route, {
                credentials: 'same-origin',
                headers: accept ? { 'Accept': accept } : undefined,
            }));
        },
    }
}
export type RequestFactory = ReturnType<typeof getRequestFactory>;

export function api(requestVerificationToken: string) {
    return Api(getRequestFactory(requestVerificationToken));
}

export enum ReadAs { Json, ValidationErrors, Text, None }
export type Handler<TReturnValue> = (value: any, response: Response) => TReturnValue
export type Failure<TReturnValue> = { handle: (error: unknown, response: Response) => TReturnValue }
export type Outcome<TReturnValue> = { handle: Handler<TReturnValue>, matches(status: number): boolean, interpretAs: ReadAs, expectedStatus?: number }

export class ApiRequest<TReturnValue> {
    private readonly promise: Promise<TReturnValue>;

    constructor(request: Promise<Response>) {
        this.promise = new Promise<TReturnValue>(async (resolve, _reject) => {
            const response = await request;
            const result = await this.handle(response);
            resolve(result);
        });
    }
    then(onfulfilled?: (value: TReturnValue) => TReturnValue | PromiseLike<TReturnValue>, onrejected?: (reason: any) => PromiseLike<never>): Promise<TReturnValue> {
        return this.promise.then(onfulfilled, onrejected);
    }
    catch(onrejected?: (reason: any) => PromiseLike<never>): Promise<TReturnValue> {
        return this.promise.catch(onrejected);
    }

    outcomes = new Array<Outcome<TReturnValue>>();
    failure: Failure<TReturnValue> = {
        handle: error => { throw error }
    };

    status(code: number, handle: Handler<TReturnValue>, interpretAs: ReadAs = ReadAs.None) {
        this.outcomes.push({
            expectedStatus: code,
            matches: statusCode => statusCode === code,
            handle,
            interpretAs,
        });
        return this;
    }
    anyStatus(handle: Handler<TReturnValue>, interpretAs: ReadAs = ReadAs.None) {
        this.outcomes.push({
            matches: () => true,
            handle,
            interpretAs,
        });
        return this;
    }
    ok(handle: Handler<TReturnValue>, interpretAs: ReadAs = ReadAs.None) {
        this.outcomes.push({
            matches: statusCode => 200 <= statusCode && statusCode < 300,
            handle,
            interpretAs,
        });
        return this;
    }
    fail(handle: (error: unknown, response: Response) => TReturnValue) {
        this.failure = {
            handle,
        };
        return this;
    }

    private async handle(response: Response) {
        let body: any;
        const { outcomes, failure } = this;
        try {
            if (response.status === 401 && !outcomes.find(x => x.expectedStatus === 401)) {
                // Authorisatiecookie verlopen/weg. Dit gebeurt soms willekeurig in Edge, zie https://stackoverflow.com/questions/53529777/ms-edge-dropping-cookie
                appInsights.trackException({
                        exception: new Error(
                            'Unexpected authentication error, did browser drop cookie or did it just expire?'),
                        properties: {
                                browser: window.navigator.userAgent,
                                url: response.url,
                                status: response.status.toString(),
                                truncatedResponseBody: JSON.stringify(body),
                                codeThatCrashed: JSON.stringify({ outcomes, failure }),
                            }
                    });
                alert('Your session has expired, you will be redirected to the home screen \n\nUw sessie is verlopen, u wordt doorgestuurd naar het welkomstscherm');
                location.replace('/');
            }
            for (let i = 0; i < outcomes.length; ++i) {
                let outcome = outcomes[i];
                if (outcome.matches(response.status)) {
                    body = await interpret(response, outcome.interpretAs);
                    return outcome.handle(body, response);
                }
            }
            throw new Error(`Unhandled status code: ${response.status}`);
        } catch (error) {
            appInsights.trackException({
                exception: error instanceof Error ? error : undefined,
                properties: {
                    url: response.url,
                    status: response.status.toString(),
                    truncatedResponseBody: JSON.stringify(body),
                    codeThatCrashed: JSON.stringify({ outcomes, failure }),
                }
            });
            return failure.handle(error, response);
        }
    }
}

async function interpret(response: Response, interpretAs: ReadAs): Promise<any> {
    switch (interpretAs) {
        case ReadAs.Json:
            return await response.json();
        case ReadAs.ValidationErrors:
            return FlattenErrors(await response.json());
        case ReadAs.Text:
            return await response.text();
    }
}
